/**
 * SC-06 — keep a shape's formatting through a rewrite (AIVA-9184).
 *
 * Writing a shape's text in one go gives all of it the first character's font, so a bold lead-in
 * ("Billing: …") turns the whole paragraph bold. Apply reads each word's font before the write, and
 * {@link planStyleRestore} puts back: each line's main font, a lead-in up to a colon, and words that
 * stood out from their line ("Below **$000M** …").
 */

/** Font properties read and restored. An absent one was mixed within the word. */
export interface TextStyle {
  bold?: boolean;
  italic?: boolean;
  underline?: string;
  color?: string;
  size?: number;
  name?: string;
}

export const STYLE_PROPS = ["bold", "italic", "underline", "color", "size", "name"] as const;

/** A run of text, by offset into the shape's text as PowerPoint reports it. */
export interface TextSpan {
  start: number;
  length: number;
}

export interface StyledWord extends TextSpan {
  style: TextStyle;
}

/** A font change on the written text. Later edits win over earlier ones. */
export interface StyleEdit extends TextSpan {
  style: TextStyle;
}

/** A lead-in ends with a colon within this many characters of its line's start. */
export const LEAD_IN_MAX_CHARS = 80;

const LEADING_PUNCTUATION = /^[("'“‘[]+/;
const EDGE_PUNCTUATION = /^[("'“‘[]+|[)"'”’\].,;:!?]+$/g;

export function wordsOf(text: string): TextSpan[] {
  return [...text.matchAll(/\S+/g)].map((m) => ({ start: m.index, length: m[0].length }));
}

interface Line {
  start: number;
  end: number;
}

/** Split at PowerPoint's paragraph (\r) and line (\v) breaks, and \n. */
function linesOf(text: string): Line[] {
  const lines: Line[] = [];
  let start = 0;
  for (const m of text.matchAll(/\r\n|[\r\n\v]/g)) {
    lines.push({ start, end: m.index });
    start = m.index + m[0].length;
  }
  lines.push({ start, end: text.length });
  return lines;
}

function styleKey(style: TextStyle): string {
  return STYLE_PROPS.map((p) => String(style[p] ?? "")).join("|");
}

/** The style covering most of the words' characters, or null for no words. */
function dominantStyle(words: readonly StyledWord[]): TextStyle | null {
  const totals = new Map<string, { style: TextStyle; chars: number }>();
  for (const w of words) {
    const key = styleKey(w.style);
    const entry = totals.get(key) ?? { style: w.style, chars: 0 };
    entry.chars += w.length;
    totals.set(key, entry);
  }
  let best: { style: TextStyle; chars: number } | null = null;
  for (const entry of totals.values()) if (!best || entry.chars > best.chars) best = entry;
  return best?.style ?? null;
}

/** Offset of the colon ending this line's lead-in, or -1. */
function leadInColon(text: string, line: Line): number {
  const colon = text.indexOf(":", line.start);
  return colon >= 0 && colon < line.end && colon - line.start < LEAD_IN_MAX_CHARS ? colon : -1;
}

interface LineStyle {
  base: TextStyle | null;
  lead: TextStyle | null;
}

/** A line's main font, and its lead-in's when the words up to a colon share a font of their own. */
function lineStyle(
  text: string,
  line: Line,
  words: readonly StyledWord[],
): { style: LineStyle; leadWords: Set<StyledWord> } {
  const colon = leadInColon(text, line);
  if (colon >= 0) {
    const leadWords = words.filter((w) => w.start <= colon);
    const leadKeys = new Set(leadWords.map((w) => styleKey(w.style)));
    // The base comes from the rest of the line: a long lead-in can be most of it.
    const base = dominantStyle(words.filter((w) => w.start > colon));
    if (leadWords.length > 0 && base && leadKeys.size === 1 && !leadKeys.has(styleKey(base))) {
      return { style: { base, lead: leadWords[0]?.style ?? null }, leadWords: new Set(leadWords) };
    }
  }
  return { style: { base: dominantStyle(words), lead: null }, leadWords: new Set() };
}

/** A word without its surrounding punctuation, which is how it's matched in the rewrite. */
function core(text: string, span: TextSpan): TextSpan & { text: string } {
  const word = text.slice(span.start, span.start + span.length);
  const lead = LEADING_PUNCTUATION.exec(word)?.[0].length ?? 0;
  const stripped = word.replace(EDGE_PUNCTUATION, "");
  return { start: span.start + lead, length: stripped.length, text: stripped };
}

interface OriginalStyles {
  lines: LineStyle[];
  /** Words in a font other than their line's, by text. */
  emphasised: Map<string, TextStyle>;
}

function readOriginal(original: string, words: readonly StyledWord[]): OriginalStyles {
  const lines: LineStyle[] = [];
  const emphasised = new Map<string, TextStyle>();
  const plain = new Set<string>();
  for (const line of linesOf(original)) {
    const inLine = words.filter((w) => w.start >= line.start && w.start < line.end);
    const { style, leadWords } = lineStyle(original, line, inLine);
    lines.push(style);
    const baseKey = style.base ? styleKey(style.base) : "";
    for (const w of inLine.filter((word) => !leadWords.has(word))) {
      const { text } = core(original, w);
      if (!text) continue;
      if (styleKey(w.style) === baseKey) plain.add(text);
      else emphasised.set(text, w.style);
    }
  }
  // A word seen both in and out of its own font says nothing about how to show it.
  for (const text of plain) emphasised.delete(text);
  return { lines, emphasised };
}

/** Each written line's main font, then its lead-in. `leads` lets the word edits skip lead-in text. */
function lineEdits(
  written: string,
  original: OriginalStyles,
  fallback: LineStyle,
): { edits: StyleEdit[]; leads: TextSpan[] } {
  const lines = linesOf(written);
  const lineForLine = lines.length === original.lines.length;
  const edits: StyleEdit[] = [];
  const leads: TextSpan[] = [];
  lines.forEach((line, i) => {
    const { base, lead } = (lineForLine ? original.lines[i] : undefined) ?? fallback;
    if (base && line.end > line.start) edits.push({ start: line.start, length: line.end - line.start, style: base });
    const colon = lead ? leadInColon(written, line) : -1;
    if (!lead || colon < 0) return;
    const start = line.start + (/^\s*/.exec(written.slice(line.start, line.end))?.[0].length ?? 0);
    const span = { start, length: colon + 1 - start };
    leads.push(span);
    edits.push({ ...span, style: lead });
  });
  return { edits, leads };
}

function wordEdits(written: string, emphasised: Map<string, TextStyle>, leads: readonly TextSpan[]): StyleEdit[] {
  const inLead = (w: TextSpan) => leads.some((l) => w.start >= l.start && w.start < l.start + l.length);
  return wordsOf(written)
    .filter((w) => !inLead(w))
    .map((w) => core(written, w))
    .flatMap((word) => {
      const style = emphasised.get(word.text);
      return style ? [{ start: word.start, length: word.length, style }] : [];
    });
}

/**
 * The font edits that give `written` (the text as PowerPoint holds it after the write) the formatting
 * `original` had. Line for line when the line count is unchanged, else the shape's main font throughout.
 * Empty when the original used one font: the write already applied it.
 */
export function planStyleRestore(original: string, words: readonly StyledWord[], written: string): StyleEdit[] {
  if (new Set(words.map((w) => styleKey(w.style))).size <= 1) return [];
  const styles = readOriginal(original, words);
  const fallback: LineStyle = { base: dominantStyle(words), lead: styles.lines.find((s) => s.lead)?.lead ?? null };
  const { edits, leads } = lineEdits(written, styles, fallback);
  return [...edits, ...wordEdits(written, styles.emphasised, leads)];
}
